import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaSearch, FaEraser, FaFilePdf, FaClipboardList } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import * as api from '~/Services/Service';
import { createDate, salvarEmExcel } from '~/Utils/Helpers';
import * as constants from '~/constant';
import Filtros from './components/Filtros';
import GridResultado from './components/GridResultado';
import ExportarExcel from './components/ExportarExcel';
import ModalGerarOcorrencias from './components/ModalGerarOcorrencias';

const URL_BASE = '/relatorios/atualizacao-cadastral';

const primeiroDiaMesAtual = () => {
  const now = new Date();
  return `01/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};
const hoje = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
};

// Datas default 1º-do-mês / hoje (Fonte: pas:1382-1383); Órgão default Prefeitura; Água default Ambos.
const FILTROS_INICIAIS = {
  dataInicial: primeiroDiaMesAtual(),
  dataFinal: hoje(),
  orgao: 'P',
  filtroAgua: 'AMBOS',
  naoConsideraEscrituraLavrada: false,
  apenasUltimaVenda: false,
  listarCedente: false,
  apresentarDetalhes: false,
  listarM2Endereco: false,
  imprimirCodigoCliente: false,
};

const AtualizacaoCadastral = () => {
  const [gesOptions, setGesOptions] = useState([]);
  const [geSelecionado, setGeSelecionado] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);

  const [filtros, setFiltros] = useState({ ...FILTROS_INICIAIS });
  const [layout, setLayout] = useState('IPTU');
  const [tiposLogradouroCustom, setTiposLogradouroCustom] = useState([]);

  const [resultado, setResultado] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [gerandoCarta, setGerandoCarta] = useState(false);
  const [showModalOcorrencias, setShowModalOcorrencias] = useState(false);
  const [gerandoOcorrencias, setGerandoOcorrencias] = useState(false);

  // Carrega GEs do usuário e auto-seleciona o primeiro (regra 15 — SetaGE). Fonte: pas:200,1370.
  useEffect(() => {
    api
      .listarGruposEmpresaAdmin({ status: 'A' })
      .then((resp) => {
        const lista = Array.isArray(resp) ? resp : [];
        const opcoes = lista.map((ge) => ({ value: ge.codigo, label: ge.apelido }));
        setGesOptions(opcoes);
        if (opcoes.length > 0) setGeSelecionado(opcoes[0]);
      })
      .catch(() => toast.error('Erro ao carregar GEs.'));
  }, []);

  // Ao trocar GE: recarrega Empresa E Empreendimento (R-LgeChange). Fonte: pas:1440-1461.
  useEffect(() => {
    let ativo = true;
    if (!geSelecionado) {
      setEmpresas([]);
      setEmpreendimentos([]);
      return undefined;
    }
    const ges = geSelecionado.value;
    Promise.all([
      api.listarEmpresasAtualizacaoCadastral(ges),
      api.listarEmpreendimentosAtualizacaoCadastral(ges),
    ])
      .then(([resEmp, resEmpr]) => {
        if (!ativo) return;
        const listEmp = Array.isArray(resEmp) ? resEmp : [];
        const listEmpr = Array.isArray(resEmpr) ? resEmpr : [];
        setEmpresas(listEmp.map((e) => ({ value: e.codigo, label: e.razao })));
        setEmpreendimentos(listEmpr.map((f) => ({ value: f.codigo_familia, label: f.nome_da_familia })));
        setEmpresaSelecionada(null);
        setEmpreendimentoSelecionado(null);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [geSelecionado]);

  const toServerDate = useCallback((str) => {
    if (!str) return null;
    const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
    return d.isValid() ? d.format(constants.DATE_FORMAT_SERVER) : null;
  }, []);

  const validar = useCallback(() => {
    if (!geSelecionado) {
      toast.warning('Selecione um GE.');
      return false;
    }
    if (!filtros.dataInicial || !filtros.dataFinal) {
      toast.warning('Preencha as datas Inicial e Final.');
      return false;
    }
    const di = createDate(filtros.dataInicial, constants.DATE_DEFAULT_FORMAT);
    const df = createDate(filtros.dataFinal, constants.DATE_DEFAULT_FORMAT);
    if (!di.isValid() || !df.isValid()) {
      toast.warning('Datas inválidas.');
      return false;
    }
    if (df.isBefore(di)) {
      toast.warning('Data Final deve ser posterior ou igual à Data Inicial.');
      return false;
    }
    return true;
  }, [geSelecionado, filtros.dataInicial, filtros.dataFinal]);

  const montarPayload = useCallback(() => ({
    codigo_grupo_empresa: geSelecionado ? geSelecionado.value : 0,
    codigo_razao_social: empresaSelecionada ? empresaSelecionada.value : null,
    codigo_familia: empreendimentoSelecionado ? empreendimentoSelecionado.value : null,
    data_inicial: toServerDate(filtros.dataInicial),
    data_final: toServerDate(filtros.dataFinal),
    orgao: filtros.orgao,
    filtro_agua: filtros.orgao === 'A' ? filtros.filtroAgua : null,
    nao_considera_escritura_lavrada: filtros.naoConsideraEscrituraLavrada,
    apenas_ultima_venda: filtros.apenasUltimaVenda,
    listar_cedente: filtros.listarCedente,
    apresentar_detalhes: filtros.apresentarDetalhes,
    listar_m2_endereco: filtros.listarM2Endereco,
    imprimir_codigo_cliente: filtros.imprimirCodigoCliente,
  }), [geSelecionado, empresaSelecionada, empreendimentoSelecionado, toServerDate, filtros]);

  const montarPayloadExport = useCallback(() => ({
    ...montarPayload(),
    layout,
    tipos_logradouro_custom: tiposLogradouroCustom.map((c) => ({ tipo1: c.tipo1, tipo2: c.tipo2 })),
  }), [montarPayload, layout, tiposLogradouroCustom]);

  const consultar = async () => {
    if (!validar()) return;
    setLoading(true);
    setResultado([]);
    try {
      const resp = await api.buscarAtualizacaoCadastral(montarPayload());
      const lista = Array.isArray(resp) ? resp : [];
      if (lista.length === 0) toast.info('Não existem dados para o período.');
      setResultado(lista);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao buscar dados.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    if (!validar()) return;
    setExportando(true);
    try {
      await salvarEmExcel(montarPayloadExport(), `${URL_BASE}/exportar-excel`);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao exportar Excel.';
      toast.error(msg);
    } finally {
      setExportando(false);
    }
  };

  const emitirCarta = async () => {
    if (!validar()) return;
    setGerandoCarta(true);
    try {
      await salvarEmExcel(montarPayload(), `${URL_BASE}/carta`);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao emitir carta.';
      toast.error(msg);
    } finally {
      setGerandoCarta(false);
    }
  };

  const confirmarOcorrencias = async () => {
    setGerandoOcorrencias(true);
    try {
      const resp = await api.gerarOcorrenciasAtualizacaoCadastral(montarPayload());
      const msg = resp && resp.mensagem ? resp.mensagem : 'Ocorrências geradas.';
      toast.success(msg);
      setShowModalOcorrencias(false);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gerar ocorrências.';
      toast.error(msg);
    } finally {
      setGerandoOcorrencias(false);
    }
  };

  const abrirModalOcorrencias = () => {
    if (!validar()) return;
    setShowModalOcorrencias(true);
  };

  const limpar = () => {
    setEmpresaSelecionada(null);
    setEmpreendimentoSelecionado(null);
    setFiltros({ ...FILTROS_INICIAIS });
    setLayout('IPTU');
    setTiposLogradouroCustom([]);
    setResultado([]);
  };

  const temResultado = resultado.length > 0;
  const quantidadeOcorrencias = new Set(resultado.map((i) => i.codigo_ativo)).size;
  const ocupado = loading || exportando || gerandoCarta || gerandoOcorrencias;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Atualização Cadastral Prefeitura/Água" />

      <Filtros
        filtros={filtros}
        setFiltros={setFiltros}
        gesOptions={gesOptions}
        geSelecionado={geSelecionado}
        onChangeGe={(opt) => setGeSelecionado(opt)}
        empresas={empresas}
        empresaSelecionada={empresaSelecionada}
        onChangeEmpresa={(opt) => setEmpresaSelecionada(opt)}
        empreendimentos={empreendimentos}
        empreendimentoSelecionado={empreendimentoSelecionado}
        onChangeEmpreendimento={(opt) => setEmpreendimentoSelecionado(opt)}
      />

      <Row className="mb-3">
        <Col className="text-right">
          <Button variant="primary" className="mr-2" onClick={consultar} disabled={ocupado}>
            {loading ? <Spinner animation="border" size="sm" className="mr-1" /> : <FaSearch className="mr-1" />}
            Consultar
          </Button>
          <Button variant="info" className="mr-2" onClick={emitirCarta} disabled={ocupado || !temResultado}>
            {gerandoCarta ? <Spinner animation="border" size="sm" className="mr-1" /> : <FaFilePdf className="mr-1" />}
            Emitir Carta Individual
          </Button>
          <Button variant="warning" className="mr-2" onClick={abrirModalOcorrencias} disabled={ocupado || !temResultado}>
            <FaClipboardList className="mr-1" />
            Gerar Ocorrências
          </Button>
          <Button variant="secondary" onClick={limpar} disabled={ocupado}>
            <FaEraser className="mr-1" />
            Limpar
          </Button>
        </Col>
      </Row>

      <ExportarExcel
        layout={layout}
        onChangeLayout={setLayout}
        tiposLogradouroCustom={tiposLogradouroCustom}
        onChangeCustom={setTiposLogradouroCustom}
        onExportar={exportarExcel}
        exportando={exportando}
        disabled={ocupado || !geSelecionado}
      />

      <GridResultado
        itens={resultado}
        listarM2Endereco={filtros.listarM2Endereco}
        imprimirCodigoCliente={filtros.imprimirCodigoCliente}
        loading={loading}
      />

      <ModalGerarOcorrencias
        show={showModalOcorrencias}
        quantidade={quantidadeOcorrencias}
        loading={gerandoOcorrencias}
        onConfirm={confirmarOcorrencias}
        onCancel={() => setShowModalOcorrencias(false)}
      />
    </Container>
  );
};

export default AtualizacaoCadastral;
